import React from 'react';
import { AlertTriangle, ShieldCheck, Upload, Download, Plus, Network, Save } from 'lucide-react';
import { Button } from '../ui/Button';
import { useGraphStore } from '../../store/graphStore';

interface AppShellProps {
    children: React.ReactNode;
}

export function AppShell({ children }: AppShellProps) {
    const meta = useGraphStore((state) => state.data.meta);
    const nodeCount = useGraphStore((state) => state.data.nodes.length);

    const [isExportOpen, setIsExportOpen] = React.useState(false);
    const [isImportOpen, setIsImportOpen] = React.useState(false);

    // Data Volatility Check - simple hook to warn on unload
    React.useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            // If we have data, warn the user
            if (nodeCount > 0) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    }, [nodeCount]);

    return (
        <div className="flex flex-col h-screen w-screen bg-background text-text-main overflow-hidden">
            {/* Top Bar */}
            <header className="flex-none h-16 border-b border-border bg-surface/50 backdrop-blur-md flex items-center justify-between px-6 z-50 relative">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-purple-600 flex items-center justify-center shadow-lg shadow-primary/20">
                        <Network className="text-white h-5 w-5" />
                    </div>
                    <div>
                        <h1 className="font-bold text-lg leading-tight tracking-tight">The Ledger</h1>
                        <div className="text-[10px] text-text-muted font-mono uppercase tracking-wider flex items-center gap-1">
                            <span className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                            Volatile Session
                        </div>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <div className="hidden md:flex items-center gap-2 mr-4 bg-orange-500/10 border border-orange-500/20 px-3 py-1.5 rounded-md text-orange-400 text-xs font-medium">
                        <AlertTriangle className="h-3 w-3" />
                        <span>Data is not saved automatically</span>
                    </div>

                    <Button variant="ghost" size="sm" className="gap-2" onClick={() => setIsImportOpen(true)}>
                        <Upload className="h-4 w-4" />
                        Import
                    </Button>
                    <Button variant="primary" size="sm" className="gap-2" onClick={() => setIsExportOpen(true)}>
                        <Download className="h-4 w-4" />
                        Export Securely
                    </Button>
                </div>
            </header>

            {/* Main Content Area */}
            <main className="flex-1 relative overflow-hidden">
                {children}
            </main>

            {/* Modals */}
            <React.Suspense fallback={null}>
                {isExportOpen && (
                    // @ts-ignore - Dynamic import to avoid circular dep if needed, or just standard import
                    <LazyExportModal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} />
                )}
                {isImportOpen && (
                    <LazyImportModal isOpen={isImportOpen} onClose={() => setIsImportOpen(false)} />
                )}
            </React.Suspense>
        </div>
    );
}

// Lazy load modals for performance
const LazyExportModal = React.lazy(() => import('../modals/ExportModal').then(m => ({ default: m.ExportModal })));
const LazyImportModal = React.lazy(() => import('../modals/ImportModal').then(m => ({ default: m.ImportModal })));
